import React, { useState, memo, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  Search, 
  Bell,
  User,
  Menu
} from 'lucide-react';
import { Popover, List, Typography, Divider } from 'antd';
import UserMenu from './UserMenu';
import DateTimeClock from '../../../components/DateTimeClock';
import GlobalSearchModal from '../../../components/GlobalSearchModal/GlobalSearchModal';
import '../layout.css';
import api from '../../../utils/api';

const MemoizedUserMenu = memo(UserMenu);
const MemoizedDateTimeClock = memo(DateTimeClock);

const Topbar = ({ user, isMobile, onMenuClick }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationPopoverVisible, setNotificationPopoverVisible] = useState(false);

  const userMenuRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const hamburgerRef = useRef(null); // NEW: Ref for hamburger button

  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on hamburger button
      if (hamburgerRef.current && hamburgerRef.current.contains(event.target)) {
        return;
      }
      
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationButtonRef.current && !notificationButtonRef.current.contains(event.target) && notificationPopoverVisible) {
        const popoverContent = document.querySelector('.ant-popover');
        if (popoverContent && !popoverContent.contains(event.target)) {
          setNotificationPopoverVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationPopoverVisible]);

  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const response = await api.get('/api/notifications/global');
      const fetchedNotifications = response.data.data || [];
      // Only show active notifications that haven't expired
      const activeNotifications = fetchedNotifications.filter(notification => 
        notification.isActive && 
        (!notification.expiresAt || new Date(notification.expiresAt) > new Date())
      );
      setNotifications(activeNotifications);
    } catch (error) {
      console.error('Error fetching global notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Optional: Keep polling every 60 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const notificationContent = (
    <div style={{ width: 350 }}>
      <Typography.Title level={5} style={{ margin: 0 }}>Global Notifications</Typography.Title>
      <Divider style={{ margin: '10px 0' }} />
      {loadingNotifications ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <Typography.Text type="secondary">No active notifications</Typography.Text>
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
          renderItem={item => (
            <List.Item>
              <List.Item.Meta
                title={item.title}
                description={
                  <>
                    <Typography.Text>{item.message}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: '0.8em' }}>
                      {new Date(item.createdAt).toLocaleString()}
                      {item.expiresAt && (
                        <>
                          {' • Expires: '}
                          {new Date(item.expiresAt).toLocaleString()}
                        </>
                      )}
                    </Typography.Text>
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const iconStyle = {
    size: 20,
    color: '#4a5568',
    strokeWidth: 1.5,
    className: 'icon-no-box'
  };

  // NEW: Improved hamburger click handler
  const handleHamburgerClick = useCallback(() => {
    console.log('Hamburger clicked - isMobile:', isMobile);
    onMenuClick();
  }, [isMobile, onMenuClick]);

  return (
    <header className="app-topbar">
      <div className="left-section">
          {isMobile && (
              <button
                  ref={hamburgerRef} // NEW: Add ref
                  className="hamburger-menu icon-btn"
                  onClick={handleHamburgerClick} // NEW: Use improved handler
                  aria-label="Toggle sidebar"
              >
                  <Menu size={20} className="icon-no-box" color={'#4a5568'} strokeWidth={1.5} />
              </button>
          )}
          <MemoizedDateTimeClock 
              showSeconds={true}
              militaryTime={false} 
          />
      </div>

      <div className="right-section">
        <div className="icon-group">
          <button
            className="icon-btn"
            onClick={() => setSearchOpen(true)}
            aria-label="Open search"
          >
            <Search {...iconStyle} />
          </button>

          <Popover
            content={notificationContent}
            trigger="click"
            open={notificationPopoverVisible}
            onOpenChange={setNotificationPopoverVisible}
            placement="bottomRight"
            overlayStyle={{ zIndex: 10000 }}
          >
            <button 
              className="icon-btn notification-btn" 
              aria-label="Notifications"
              ref={notificationButtonRef}
            >
              <Bell {...iconStyle} />
              {notifications.length > 0 && (
                <span className="notification-dot"></span>
              )}
            </button>
          </Popover>
        </div>

        <div className="user-menu-container-wrapper" ref={userMenuRef}>
          <div
            className="user-avatar-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-haspopup="true"
            aria-expanded={showUserMenu}
            aria-label="User menu"
          >
            <User {...iconStyle} />
            <span className="user-avatar-text">
              {user?.name?.split(' ')[0] || user?.email?.split('@')[0]}
            </span>
          </div>
          
          {showUserMenu && (
            <div className="user-menu-position-wrapper">
              <MemoizedUserMenu 
                user={user} 
                onClose={() => setShowUserMenu(false)} 
              />
            </div>
          )}
        </div>
      </div>

      <GlobalSearchModal
          visible={searchOpen}
          onClose={() => setSearchOpen(false)}
      />
    </header>
  );
};

Topbar.propTypes = {
    user: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string.isRequired,
        role_name: PropTypes.string
    }).isRequired,
    isMobile: PropTypes.bool.isRequired,
    onMenuClick: PropTypes.func.isRequired
};

export default memo(Topbar);